import i18next from 'i18next';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ComboBoxResponsive } from '@/components/ui/combobox-responsive';
import { IUnit } from '@/interfaces/IUnit';
import { useAssignedUnits } from '@/queries/useAssignedUnits';
import { useMissionReportQuestionary } from '@/queries/useMissionReportQuestinary';

import { SELECTED_UNIT_ID } from './helpers';
import MissionReportQuestionary from './MissionReportQuestionary';

const QuestionaryComponent = ({ unitId }: { unitId: string }) => {
  const { data: questionary, isLoading: isLoadingQuestionary } = useMissionReportQuestionary({
    unitId
  });

  if (isLoadingQuestionary || !questionary) {
    return (
      <div className="flex items-center justify-center mt-4">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return <MissionReportQuestionary key={unitId} unitId={unitId} questionary={questionary} />;
};

const UnitsSelectAndQuestionary = ({ units }: { units: IUnit[] }) => {
  const storedUnitId = localStorage.getItem(SELECTED_UNIT_ID);
  const defaultUnitId = units.some(unit => unit.id.toString() === storedUnitId)
    ? (storedUnitId as string)
    : units[0].id.toString();
  const [selectedUnitId, setSelectedUnitId] = useState<string>(defaultUnitId);

  const unitsOptions = units.map(unit => ({
    label: unit.name,
    value: unit.id.toString()
  }));

  return (
    <div className="max-w-md mx-auto p-4">
      <h1 className="text-3xl pb-1">{i18next.t('mission_report.title')}</h1>

      {unitsOptions.length > 1 && (
        <div className="my-2 flex flex-col w-full">
          <ComboBoxResponsive
            drawerTitle={i18next.t('placeholders.select_unit')}
            groups={[
              {
                title: i18next.t('mission_report.unit'),
                options: unitsOptions
              }
            ]}
            value={selectedUnitId}
            onChange={unitId => {
              localStorage.setItem(SELECTED_UNIT_ID, unitId);
              setSelectedUnitId(unitId);
            }}
          />
        </div>
      )}

      <p className="text-sm text-muted-foreground pb-8">{i18next.t('mission_report.subtitle')}</p>

      {selectedUnitId && <QuestionaryComponent unitId={selectedUnitId} />}
    </div>
  );
};

const MissionReport = () => {
  const { data: units, isLoading: isLoadingUnits } = useAssignedUnits();

  if (isLoadingUnits || !units || units.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return <UnitsSelectAndQuestionary units={units} />;
};

export default MissionReport;
